import * as Sentry from "@sentry/react";

function captureSubmissionError(formName, responseStatus, responseBody) {
  Sentry.captureException(new Error(`${formName} submission failed: ${responseStatus}`), {
    tags: { action: "FormSubmission", form: formName },
    extra: { responseStatus, responseBody },
  });
}

function reportDoableLoadFailure(err, { action, doableId }) {
  if (err instanceof Error) {
    // A dropped connection needs no report. Any other error, including a
    // TypeError from a programming fault, does.
    if (!isNetworkError(err)) {
      Sentry.captureException(err, { tags: { action }, extra: { doableId } });
    }
    return;
  }

  // Routine client-error responses need no report; a server error has no other
  // signal because the page shows the same failure card for both.
  if (err?.status >= 500) {
    Sentry.captureMessage("Loading a doable returned a server error", {
      level: "error",
      tags: { action },
      extra: { doableId, status: err.status },
    });
  }
}

// The public share page's code lookup. An unknown code is a normal response
// (referralCodeValid false), so anything that throws here is a real failure;
// a dropped connection needs no report.
function reportReferralCodeLookupFailure(err) {
  if (isNetworkError(err)) return;
  Sentry.captureException(err, {
    tags: { action: "ReferralCodeLookup" },
    extra: { status: err?.status },
  });
}

// Classifies a thrown value as a browser fetch-level network failure.
// WebKit reports these as TypeError("Load failed"); Chromium/Firefox as TypeError("Failed to fetch").
// v10’s enhanceFetchErrorMessages appends ‘ (hostname)’, so match on substring.
// Chromium’s “Failed to fetch dynamically imported module” is a chunk that
// didn’t load, which a stale bundle after a deploy can cause too.
function isNetworkError(error) {
  return (
    error instanceof TypeError &&
    typeof error.message === "string" &&
    !error.message.includes("dynamically imported module") &&
    (error.message.includes("Load failed") || error.message.includes("Failed to fetch"))
  );
}

// A signup id is the only thing POST /api/probate/signup/{id}/instruct asks
// for, so a resume URL in Sentry is a 30-day credential for reading a client's
// answers and instructing in their name. The first block is left in to find
// the row with `where id::text like '3f1b2c4d%'`: 32 of the id's 122 bits,
// nowhere near enough to guess the rest.
const SIGNUP_ID_IN_URL = /(\/probate\/signup\/)([0-9a-f]{8})-[0-9a-f-]{27}/gi;

function redactSignupId(url) {
  return typeof url === "string" ? url.replace(SIGNUP_ID_IN_URL, "$1$2-redacted") : url;
}

export {
  captureSubmissionError,
  isNetworkError,
  redactSignupId,
  reportDoableLoadFailure,
  reportReferralCodeLookupFailure,
};
